import React, { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Trash2 } from "lucide-react";
import { facilitiesApi } from "../../../lib/api";
import { getApiError } from "../../../lib/utils";

export const DeleteFacilityModal = ({ facility, onClose, onDeleted }) => {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await facilitiesApi.remove(facility.facility_id, reason.trim() || null);
      toast.success(`${facility.name} deleted`);
      onDeleted?.();
    } catch (e) {
      toast.error(getApiError(e, "Delete failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 p-4" onClick={() => !busy && onClose()}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md overflow-hidden rounded-lg border-2 border-red-300 bg-white shadow-2xl"
        data-testid="facility-delete-modal"
      >
        <div className="flex items-start gap-3 border-b border-slate-200 p-5">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100">
            <AlertTriangle size={20} className="text-red-700" />
          </div>
          <div className="flex-1">
            <h3 className="font-heading text-lg font-bold text-slate-900">Delete this facility?</h3>
            <div className="mt-1 rounded bg-slate-50 px-2 py-1 text-xs">
              <div className="font-semibold text-slate-900">{facility.name}</div>
              <div className="font-mono text-[10px] text-slate-500">
                {facility.facility_id} · {facility.category || "—"} · {facility.town || "—"}, {facility.region || "—"}
              </div>
            </div>
            <p className="mt-2 text-xs text-slate-600">
              This <b>permanently removes</b> the provider. A snapshot is kept in the
              audit log for reference, but it will no longer appear anywhere in the app.
            </p>
          </div>
        </div>

        <div className="p-5">
          <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Reason (optional)</div>
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Duplicate, decommissioned, closed…"
            className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-nmi-600"
            data-testid="facility-delete-reason"
            disabled={busy}
            maxLength={500}
          />
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
          <button
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            data-testid="facility-delete-cancel"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy}
            autoFocus
            data-testid="facility-delete-submit"
            className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-4 py-2 text-xs font-bold uppercase tracking-wider text-white hover:bg-red-700 disabled:opacity-50"
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} strokeWidth={2.5} />}
            Yes, delete
          </button>
        </div>
      </div>
    </div>
  );
};
